// A dense, sortable table for the list pages.
//
// These pages used to be sparse rows: a title, a muted subline, and whatever fitted.
// That reads fine with six records and badly with sixty — you can't scan a column that
// doesn't exist, and you can't sort by something you can't see. A table gives every
// fact its own column, so comparing across records becomes possible instead of a
// matter of reading each row in turn.
//
// Sorting lives in the column headers rather than a separate control: the header IS
// the affordance, and it means the sort key and the thing being sorted are the same
// object on screen.

import { useMemo, useState, type ReactNode } from 'react'

import { Icon } from './Icon'

export interface Column<T> {
  key: string
  label: string
  /** the sortable value; strings sort case-insensitively, numbers numerically */
  value?: (row: T) => string | number
  render: (row: T) => ReactNode
  align?: 'left' | 'right'
  /** a fixed width keeps numeric columns from dancing as you filter */
  width?: number | string
  /** a width that only applies at a container size, e.g. `@4xl:w-[32%]`. Capping a
   *  flexible column is only right while some other column is there to take the slack:
   *  once the wide-only columns drop out, the cap would hand the leftover space to the
   *  fixed numeric columns, which then drift away from the text they belong to. */
  widthClass?: string
  /** drop this column when the container is narrower than this many px */
  hideBelow?: HideBelow
  /** columns with nothing to compare (a link, an icon) opt out of sorting */
  sortable?: boolean
}

// Container widths, not viewport widths: the sheet is ~1120px alone and ~720px with
// the peek panel open, and a column that only knows the window size gets clipped.
//
// The tiers are named for the width they need rather than sm/md/lg, which read like
// viewport breakpoints and hid the real question — how much room does the table need
// before this column earns its place? The Work table's Link column sat on the 1024
// tier and vanished at 1000px, which is plenty of room for it.
const HIDE = {
  448: 'hidden @md:table-cell',
  672: 'hidden @2xl:table-cell',
  768: 'hidden @3xl:table-cell',
  896: 'hidden @4xl:table-cell',
  1024: 'hidden @5xl:table-cell',
} as const
type HideBelow = keyof typeof HIDE

function compare(a: string | number, b: string | number): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b
  return String(a).localeCompare(String(b), undefined, { sensitivity: 'base', numeric: true })
}

export function DataTable<T>({
  rows,
  columns,
  rowKey,
  onOpen,
  selected,
  onOpenFull,
  openLabel,
  rowClass,
  initialSort,
  storageKey,
  empty,
}: {
  rows: T[]
  columns: Column<T>[]
  rowKey: (row: T) => string
  /** what a click means — usually "peek at this", not "navigate away" */
  onOpen?: (row: T) => void
  /** the row the peek panel is showing, marked with an accent edge */
  selected?: string | undefined
  /**
   * Straight to the record's own page. Rendered as an arrow that appears on row
   * hover: peeking is the common case, but wanting the whole thing shouldn't cost
   * a peek first.
   */
  onOpenFull?: (row: T) => void
  /** what to call that arrow, per row: "Open Tidewater viewer", not the 18th "Open" */
  openLabel?: (row: T) => string
  rowClass?: (row: T) => string
  initialSort?: { key: string; dir: 'asc' | 'desc' }
  /** remembers the sort per list, per device — a view pref, not shared data */
  storageKey?: string
  empty?: ReactNode
}) {
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' }>(() => {
    if (storageKey && typeof localStorage !== 'undefined') {
      const raw = localStorage.getItem(storageKey)
      if (raw) {
        try {
          const parsed = JSON.parse(raw) as { key: string; dir: 'asc' | 'desc' }
          if (parsed?.key) return parsed
        } catch {
          /* fall through to the default */
        }
      }
    }
    return initialSort ?? { key: columns[0]?.key ?? '', dir: 'asc' }
  })

  const change = (key: string) => {
    const next: { key: string; dir: 'asc' | 'desc' } =
      sort.key === key ? { key, dir: sort.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
    setSort(next)
    if (storageKey) {
      try {
        localStorage.setItem(storageKey, JSON.stringify(next))
      } catch {
        /* losing a view pref costs nothing */
      }
    }
  }

  const sorted = useMemo(() => {
    const col = columns.find((c) => c.key === sort.key)
    if (!col?.value) return rows
    const dir = sort.dir === 'asc' ? 1 : -1
    return [...rows].sort((a, b) => compare(col.value!(a), col.value!(b)) * dir)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sort, columns])

  if (rows.length === 0 && empty) return <>{empty}</>

  return (
    <div className="table-wrap -mx-1 px-1">
      <table className="w-full border-collapse" style={{ fontSize: 'var(--text-body)' }}>
        <thead>
          <tr>
            {columns.map((c) => {
              const sortable = c.sortable !== false && !!c.value
              const active = sort.key === c.key
              return (
                <th
                  key={c.key}
                  className={`${c.hideBelow ? HIDE[c.hideBelow] : ''} ${c.widthClass ?? ''} sticky z-10 border-b pt-1.5 pr-3 pb-2 text-left align-bottom whitespace-nowrap`}
                  style={{
                    // Parks under the page header — except where the wrapper has
                    // become the scrollport, where the only correct offset is zero.
                    // See .table-wrap in global.css.
                    top: 'var(--table-head-top, var(--page-header-h))',
                    background: 'var(--bg)',
                    borderColor: 'var(--border)',
                    width: c.width,
                    textAlign: c.align ?? 'left',
                  }}
                >
                  {sortable ? (
                    <button
                      type="button"
                      onClick={() => change(c.key)}
                      className="inline-flex items-center gap-1"
                      style={{
                        fontFamily: 'var(--mono)',
                        fontSize: 'var(--text-micro)',
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        color: active ? 'var(--fg)' : 'var(--faint)',
                      }}
                      title={`Sort by ${c.label.toLowerCase()}`}
                    >
                      {c.label}
                      {/* The caret only appears on the active column: an arrow on every
                          header is noise, and one arrow answers "sorted by what". */}
                      <span
                        aria-hidden
                        style={{ opacity: active ? 1 : 0, fontSize: 'var(--text-micro)' }}
                      >
                        {sort.dir === 'asc' ? '▲' : '▼'}
                      </span>
                    </button>
                  ) : (
                    <span className="eyebrow">{c.label}</span>
                  )}
                </th>
              )
            })}
            {onOpenFull && (
              <th
                style={{
                  width: 26,
                  background: 'var(--bg)',
                  top: 'var(--table-head-top, var(--page-header-h))',
                }}
                className="sticky border-b"
              />
            )}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => (
            <tr
              key={rowKey(row)}
              onClick={onOpen ? () => onOpen(row) : undefined}
              aria-selected={selected === rowKey(row)}
              className={`group ${onOpen ? 'cursor-pointer hover:bg-[var(--hover)]' : ''} ${rowClass?.(row) ?? ''}`}
              style={{
                borderBottom: '1px solid var(--rule-soft)',
                ...(selected === rowKey(row)
                  ? { background: 'var(--hover)', boxShadow: 'inset 2px 0 0 var(--accent)' }
                  : {}),
              }}
            >
              {columns.map((c) => (
                <td
                  key={c.key}
                  className={`${c.hideBelow ? HIDE[c.hideBelow] : ''} py-2 pr-3 align-middle`}
                  style={{ textAlign: c.align ?? 'left' }}
                >
                  {c.render(row)}
                </td>
              ))}
              {onOpenFull && (
                <td className="py-2 align-middle">
                  <button
                    type="button"
                    title={openLabel ? `Open ${openLabel(row)}` : 'Open this record'}
                    aria-label={openLabel ? `Open ${openLabel(row)}` : 'Open this record'}
                    onClick={(e) => {
                      e.stopPropagation()
                      onOpenFull(row)
                    }}
                    className="rounded-inline flex h-6 w-6 items-center justify-center opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
                    style={{ color: 'var(--muted)' }}
                  >
                    <Icon name="ArrowRight" size={14} />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** A number that reads as a number: mono, tabular, and quiet when it's zero. */
export function Num({ value, hue }: { value: number | string; hue?: string }) {
  const zero = value === 0 || value === '' || value === '·'
  return (
    <span
      className="num"
      style={{
        fontSize: 'var(--text-meta)',
        color: zero ? 'var(--faint)' : hue ? `var(${hue})` : 'var(--fg)',
      }}
    >
      {zero ? '·' : value}
    </span>
  )
}
